/**
 * DocumentTypeResponsablesPopover — spec rev74 (2026-08-02)
 * ----------------------------------------------------------
 * Popover unique et centralisé pour gérer la matrice de droits
 * (utilisateurs × actions) pour UN type de document donné.
 *
 * Ce popover remplace `UserTypePermissionsDialog` (précédemment accessible
 * depuis Paramètres > Utilisateurs > Droits par type de document). Il
 * devient l'UNIQUE point d'entrée pour piloter cette matrice — plus de
 * duplication d'accès dans le pod.
 *
 * Actions cochables : view · create · edit · advance (Voir · Créer ·
 *   Modifier · Faire avancer).
 * Action verrouillée : close (Clôturer) — icône cadenas immuable,
 *   jamais persistée, réservée aux admins.
 *
 * Règles métier (backend-enforced, rappel UI) :
 *   • Créer un TYPE reste réservé aux admins (l'ouverture de ce popover
 *     l'est aussi via `_is_admin`).
 *   • Créer un DOCUMENT et faire avancer les étapes peuvent être délégués
 *     via la ligne `create` / `advance`.
 *   • Clôturer un document reste toujours admin-only (`close` filtré).
 *
 * Endpoints :
 *   GET  /api/user-type-permissions/by-type/{type_id}/entries
 *   PUT  /api/user-type-permissions/by-type/{type_id}
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "@/lib/toast";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { LockIcon as LockIcon, UserPlus, X, ChevronDown, ChevronUp, Loader2, Search, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";
import { AvatarBlock } from "@/components/settings/AccountSection";

const ACTIONS = [
  { key: "view",    label: "Voir",          tip: "Voir les documents de ce type dans la liste et le détail" },
  { key: "create",  label: "Créer",         tip: "Créer un nouveau document de ce type" },
  { key: "edit",    label: "Modifier",      tip: "Modifier les métadonnées et les valeurs" },
  { key: "advance", label: "Faire avancer", tip: "Uploader des fichiers d'étape et remplir les champs requis" },
];
const DEFAULT_ACTIONS = ["view", "create", "edit", "advance"];

export default function DocumentTypeResponsablesPopover({
  type,              // required en mode "type"
  category,          // required en mode "category" (rev77)
  mode = "type",     // "type" | "category"
  users,             // toutes les utilisateurs (déjà filtrés is_deleted côté parent)
  onSaved,           // callback après enregistrement (parent recharge)
  triggerAsAvatars,  // ReactNode : les avatars empilés qui déclenchent l'ouverture
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // matrix : { user_id -> Set(actions) }
  const [matrix, setMatrix] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  // Non-admins uniquement (admins ont accès total automatique).
  const nonAdmins = useMemo(
    () => (users || []).filter(
      (u) => !u.is_first_admin && u.role !== "admin" && !u.is_deleted,
    ),
    [users],
  );

  const targetId = mode === "category" ? category?.id : type?.id;
  const endpointBase = mode === "category"
    ? "/user-type-permissions/by-category"
    : "/user-type-permissions/by-type";
  const targetLabel = mode === "category" ? category?.name : type?.name;
  const targetColor = mode === "category" ? category?.color : type?.color;

  const load = async () => {
    if (!targetId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`${endpointBase}/${targetId}/entries`);
      // Spec rev138 (2026-02-06) — normalise `actions` : les entrées legacy
      // en base sont parfois stockées comme dict
      // `{view:true, create:false, modify:false, suppress:false}`. Convertit
      // en Set de clés canoniques (view/create/edit/advance).
      const KEY_MAP = { modify: "edit", suppress: "advance" };
      const m = {};
      (data?.entries || []).forEach((e) => {
        let acts = e.actions;
        if (Array.isArray(acts)) {
          m[e.user_id] = new Set(acts);
        } else if (acts && typeof acts === "object") {
          const s = new Set();
          for (const [k, v] of Object.entries(acts)) {
            if (!v) continue;
            s.add(KEY_MAP[k] || k);
          }
          m[e.user_id] = s;
        } else {
          m[e.user_id] = new Set();
        }
      });
      setMatrix(m);
    } catch (err) {
      toast.error("Impossible de charger les droits", { description: err.response?.data?.detail || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) load(); }, [open, targetId]); // eslint-disable-line react-hooks/exhaustive-deps

  const assignedUsers = useMemo(
    () => nonAdmins
      .filter((u) => matrix[u.id] && matrix[u.id].size > 0)
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "fr")),
    [nonAdmins, matrix],
  );

  const unassignedUsers = useMemo(
    () => nonAdmins
      .filter((u) => !matrix[u.id] || matrix[u.id].size === 0)
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`, "fr")),
    [nonAdmins, matrix],
  );

  const toggleAction = (userId, action) => {
    setMatrix((prev) => {
      const next = { ...prev };
      const cur = new Set(next[userId] || []);
      if (cur.has(action)) cur.delete(action);
      else cur.add(action);
      next[userId] = cur;
      return next;
    });
  };

  const removeUser = (userId) => {
    setMatrix((prev) => {
      const next = { ...prev };
      delete next[userId];
      return next;
    });
  };

  const addUser = (userId) => {
    setMatrix((prev) => ({ ...prev, [userId]: new Set(DEFAULT_ACTIONS) }));
    setPickerSearch("");
    // On garde le picker ouvert pour ajout multiple rapide.
  };

  const filteredUnassigned = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return unassignedUsers;
    return unassignedUsers.filter((u) => {
      const nm = `${u.first_name || ""} ${u.last_name || ""} ${u.email || ""}`.toLowerCase();
      return nm.includes(q);
    });
  }, [unassignedUsers, pickerSearch]);

  const save = async () => {
    setSaving(true);
    try {
      const entries = Object.entries(matrix)
        .filter(([, actions]) => actions && actions.size > 0)
        .map(([userId, actions]) => ({
          user_id: userId,
          // Le backend ignore ce champ pour by-type et by-category, mais
          // on le remplit avec l'id cible pour cohérence côté client.
          type_id: mode === "type" ? targetId : "",
          actions: Array.from(actions).filter((a) => a !== "close"),
        }));
      await api.put(`${endpointBase}/${targetId}`, { entries });
      toast.success("Responsables enregistrés");
      setOpen(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error("Échec", { description: err.response?.data?.detail || "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerAsAvatars}
      </PopoverTrigger>
      <PopoverContent
        side="left"
        align="start"
        sideOffset={8}
        collisionPadding={12}
        className="p-0 border-0 shadow-2xl"
        style={{ width: 560, background: "transparent" }}
        data-testid={`type-perms-popover-${targetId}`}
      >
        <div
          className="rounded-lg overflow-hidden"
          style={{ background: "#0F0F10", border: "1px solid #2A2A2A" }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-3 border-b"
            style={{ borderColor: "#242424", background: "#141414" }}
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                {mode === "category" ? "Responsables · Catégorie" : "Responsables"}
              </div>
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: targetColor || "#6B7280" }}
                />
                {targetLabel}
              </div>
            </div>
            <span className="text-[10px] text-muted-foreground max-w-[220px] text-right leading-tight">
              {mode === "category"
                ? "Droits hérités par tous les types de cette catégorie (actuels et futurs). La clôture reste réservée aux administrateurs."
                : "La clôture reste réservée aux administrateurs."}
            </span>
          </div>

          {/* Body */}
          <div className="p-3 max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground text-xs italic">
                <Loader2 size={14} className="animate-spin mr-2" />
                Chargement…
              </div>
            ) : (assignedUsers.length === 0 && !pickerOpen) ? (
              // Spec iter129 pt.2/3 (Feb 2026) — état vide visuel simple
              // (retire le tableau de permissions VOIR/CRÉER/…) avec
              // bordure pointillés, icône silhouettes, texte adapté au
              // mode (type / catégorie) et gros bouton « + Ajouter ».
              <div
                className="flex flex-col items-center justify-center gap-4 py-8 px-4 rounded-lg"
                style={{
                  border: `1.5px dashed ${targetColor ? `${targetColor}55` : "#3A3A3A"}`,
                  background: targetColor ? `${targetColor}0A` : "transparent",
                }}
                data-testid={`type-perms-empty-${targetId}`}
              >
                <UsersIcon
                  size={40}
                  strokeWidth={1.6}
                  style={{ color: targetColor || "#6B7280" }}
                />
                <div className="text-center text-[13px] italic" style={{ color: "#B4B7BB" }}>
                  Aucun responsable défini pour {mode === "category" ? "cette catégorie" : "ce type"}
                </div>
                <Button
                  type="button"
                  size="sm"
                  className="h-9 gap-2 px-4 text-[13px] font-semibold border-0"
                  style={{
                    // Spec iter129 (Feb 2026) — bouton sombre pour garantir
                    // un contraste texte élevé quel que soit le targetColor
                    // (couleurs pastel comme violet Juridique inclus). La
                    // couleur cible sert d'accent : bordure + icône.
                    background: "#1E1E1E",
                    color: targetColor || "#EDEDED",
                    boxShadow: `inset 0 0 0 1.5px ${targetColor || "#2B579A"}`,
                  }}
                  disabled={nonAdmins.length === 0}
                  onClick={() => setPickerOpen(true)}
                  data-testid={`type-perms-add-empty-${targetId}`}
                >
                  <UserPlus size={14} strokeWidth={2.4} />
                  Ajouter un responsable
                </Button>
              </div>
            ) : (
              <>
                {assignedUsers.length > 0 && (
                  <>
                    {/* Table header */}
                    <div className="grid text-[10px] uppercase tracking-wider text-muted-foreground pb-1.5 border-b border-white/5"
                      style={{ gridTemplateColumns: "1fr 60px 60px 60px 90px 40px 26px" }}>
                      <div>Utilisateur</div>
                      {ACTIONS.map((a) => (
                        <div key={a.key} className="text-center" title={a.tip}>{a.label}</div>
                      ))}
                      <div className="text-center opacity-50" title="Réservé aux admins">Clôturer</div>
                      <div></div>
                    </div>

                    {/* Rows */}
                    <ul className="space-y-1 mt-1.5">
                    {assignedUsers.map((u) => {
                      const set = matrix[u.id] || new Set();
                      return (
                        <li
                          key={u.id}
                          className="grid items-center py-1.5 border-b border-white/5"
                          style={{ gridTemplateColumns: "1fr 60px 60px 60px 90px 40px 26px" }}
                          data-testid={`type-perms-row-${u.id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <AvatarBlock
                              initials={`${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`}
                              size={26}
                              seed={u.id || u.email}
                              name={`${u.first_name} ${u.last_name}`.trim()}
                            />
                            <div className="min-w-0 truncate text-xs text-white">
                              {u.first_name} {u.last_name}
                            </div>
                          </div>
                          {ACTIONS.map((a) => (
                            <div key={a.key} className="text-center">
                              <Checkbox
                                checked={set.has(a.key)}
                                onCheckedChange={() => toggleAction(u.id, a.key)}
                                data-testid={`type-perms-${u.id}-${a.key}`}
                              />
                            </div>
                          ))}
                          <div className="text-center">
                            <LockIcon
                              size={12}
                              className="mx-auto text-muted-foreground opacity-60"
                              title="Verrou métier — réservé aux administrateurs"
                            />
                          </div>
                          <div className="text-center">
                            <button
                              type="button"
                              onClick={() => removeUser(u.id)}
                              className="text-muted-foreground hover:text-red-400 transition"
                              title="Retirer ce responsable"
                              data-testid={`type-perms-remove-${u.id}`}
                            >
                              <X size={13} />
                            </button>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  </>
                )}

                {/* Add user — spec rev138 + rev143 (2026-02-06) : panneau
                    inline expansible avec message contextuel clair quand
                    aucun utilisateur non-admin n'existe (cohérence UX
                    avec RestrictionDialog). */}
                <div className="pt-3 mt-3 border-t border-white/5">
                  {!pickerOpen ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-[12px]"
                      disabled={nonAdmins.length === 0}
                      onClick={() => setPickerOpen(true)}
                      data-testid={`type-perms-add-btn-${targetId}`}
                      title={
                        nonAdmins.length === 0
                          ? "Aucun utilisateur non-admin dans le système — créez d'abord un compte Manager ou Utilisateur."
                          : unassignedUsers.length === 0
                            ? "Tous les utilisateurs non-admin sont déjà assignés."
                            : "Ajouter un responsable"
                      }
                    >
                      <UserPlus size={13} />
                      Ajouter un responsable
                      <ChevronDown size={11} />
                    </Button>
                  ) : (
                    <div
                      className="rounded-md overflow-hidden"
                      style={{ background: "#141414", border: "1px solid #2A2A2A" }}
                      data-testid={`type-perms-picker-${targetId}`}
                    >
                      <div
                        className="flex items-center gap-2 px-2.5 py-2"
                        style={{ borderBottom: "1px solid #2A2A2A", background: "#1A1A1A" }}
                      >
                        <Search size={12} style={{ color: "#6B7280" }} />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Rechercher un utilisateur…"
                          value={pickerSearch}
                          onChange={(e) => setPickerSearch(e.target.value)}
                          className="flex-1 h-6 bg-transparent focus:outline-none text-[12px]"
                          style={{ color: "#EDEDED" }}
                          data-testid={`type-perms-picker-search-${targetId}`}
                        />
                        <button
                          type="button"
                          onClick={() => { setPickerOpen(false); setPickerSearch(""); }}
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded hover:bg-white/10 flex items-center gap-0.5"
                          style={{ color: "#9CA3AF" }}
                          data-testid={`type-perms-picker-close-${targetId}`}
                        >
                          Fermer <ChevronUp size={11} />
                        </button>
                      </div>
                      <div className="max-h-[200px] overflow-y-auto py-1">
                        {filteredUnassigned.length === 0 ? (
                          <div className="px-3 py-3 text-[11.5px] italic text-center leading-relaxed" style={{ color: "#8B8F94" }}>
                            {pickerSearch.trim()
                              ? "Aucun résultat pour cette recherche."
                              : nonAdmins.length === 0
                                ? <>
                                    Aucun utilisateur <strong>non-admin</strong> dans le système.<br />
                                    Les admins ont déjà accès automatiquement.<br />
                                    <span style={{ color: "#5A9EE0" }}>Créez d'abord un compte Manager ou Utilisateur dans Paramètres → Utilisateurs.</span>
                                  </>
                                : "Tous les utilisateurs non-admin sont déjà assignés."}
                          </div>
                        ) : filteredUnassigned.map((u) => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => addUser(u.id)}
                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[12px] hover:bg-white/5 transition"
                            data-testid={`type-perms-add-${u.id}`}
                          >
                            <AvatarBlock
                              initials={`${u.first_name?.[0] || ""}${u.last_name?.[0] || ""}`}
                              size={22}
                              seed={u.id || u.email}
                              name={`${u.first_name} ${u.last_name}`.trim()}
                            />
                            <span className="truncate text-white">{u.first_name} {u.last_name}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div
            className="px-3 py-2.5 border-t flex items-center justify-end gap-2"
            style={{ borderColor: "#242424", background: "#141414" }}
          >
            <Button variant="ghost" size="sm" className="h-8" onClick={() => setOpen(false)} disabled={saving}>
              Annuler
            </Button>
            <Button
              size="sm"
              className="h-8"
              onClick={save}
              disabled={saving || loading}
              data-testid={`type-perms-save-${targetId}`}
            >
              {saving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
